import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FileText, Plus, Info } from "lucide-react";
import { VentureCard, VentureCardHeader, VentureCardTitle, VentureCardContent } from "@/components/ui/venture-card";
import { Button } from "@/components/ui/button";
import { useEODContext } from "@/components/eod/EODProvider";
import { useRole } from "@/contexts/RoleContext";
import { supabase } from "@/integrations/supabase/client";
import { PageTour } from "@/components/tour/PageTour";
import { clientReportsSteps } from "@/lib/tour-steps";

export default function ClientReports() {
  const { openForm, submissions } = useEODContext();
  const { clientPackage } = useRole();
  const [avgCTR, setAvgCTR] = useState<string | null>(null);
  const [ctrLoading, setCtrLoading] = useState(true);

  const demoPrice = clientPackage === 'elite' ? 200 : 150;
  const thisWeekDemos = submissions.slice(0, 7).reduce((sum, s) => sum + s.demos_booked, 0);
  const completionRate = Math.min(100, Math.round((submissions.length / 7) * 100));
  const avgQuality = submissions.length > 0
    ? (submissions.reduce((sum, s) => sum + ({'excellent': 4, 'good': 3, 'fair': 2, 'poor': 1}[s.lead_quality] || 0), 0) / submissions.length).toFixed(1)
    : '0.0';

  useEffect(() => {
    const fetchCTR = async () => {
      setCtrLoading(true);
      try {
        const clientId = localStorage.getItem('active_client_id');
        if (!clientId) {
          setAvgCTR(null);
          return;
        }

        const { data, error } = await supabase
          .from('campaign_metrics')
          .select('clicks, impressions')
          .eq('client_id', clientId);

        if (error) throw error;

        if (!data || data.length === 0) {
          setAvgCTR(null);
          return;
        }

        const totalClicks = data.reduce((sum, row) => sum + (row.clicks || 0), 0);
        const totalImpressions = data.reduce((sum, row) => sum + (row.impressions || 0), 0);

        if (totalImpressions === 0) {
          setAvgCTR(null);
          return;
        }

        setAvgCTR((totalClicks / totalImpressions * 100).toFixed(1));
      } catch (err) {
        console.error('Failed to fetch campaign metrics:', err);
        setAvgCTR(null);
      } finally {
        setCtrLoading(false);
      }
    };

    fetchCTR();
  }, []);

  return (
    <>
    <PageTour steps={clientReportsSteps} />
    <motion.div className="space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Reports</h1>
          <p className="text-muted-foreground">EOD reports and performance tracking</p>
        </div>
        <Button onClick={() => openForm()} className="bg-gradient-to-r from-primary to-secondary">
          <Plus className="w-4 h-4 mr-2" />
          Submit End-of-Day
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <VentureCard><VentureCardContent className="text-center"><p className="text-3xl font-bold">{thisWeekDemos}</p><p className="text-sm text-muted-foreground">Demos This Week</p></VentureCardContent></VentureCard>
        <VentureCard><VentureCardContent className="text-center"><p className="text-3xl font-bold">{completionRate}%</p><p className="text-sm text-muted-foreground">EOD Completion</p></VentureCardContent></VentureCard>
        <VentureCard><VentureCardContent className="text-center"><p className="text-3xl font-bold">{avgQuality}</p><p className="text-sm text-muted-foreground">Avg Lead Quality</p></VentureCardContent></VentureCard>
        <VentureCard>
          <VentureCardContent className="text-center">
            <p className="text-3xl font-bold">
              {ctrLoading ? (
                <span className="inline-block w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              ) : avgCTR ? (
                `${avgCTR}%`
              ) : (
                'N/A'
              )}
            </p>
            <p className="text-sm text-muted-foreground">Avg Ad Performance (CTR)</p>
          </VentureCardContent>
        </VentureCard>
      </div>

      {/* Info Banner */}
      <div className="p-4 rounded-lg bg-secondary/10 border border-secondary/30 flex items-start gap-3">
        <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
        <p className="text-sm">The EOD form will automatically pop up every evening at 7 PM if you haven't submitted your report for the day. You can also submit anytime using the button above.</p>
      </div>

      {/* EOD Records */}
      <VentureCard>
        <VentureCardHeader><VentureCardTitle>EOD Records</VentureCardTitle></VentureCardHeader>
        <VentureCardContent>
          {submissions.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No EOD reports submitted yet</p>
              <Button onClick={() => openForm()} className="mt-4">Submit Your First Report</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead><tr className="border-b border-border"><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Demos Booked</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Demos Showed</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Lead Quality</th><th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Show Rate</th></tr></thead>
                <tbody>
                  {submissions.slice(0, 10).map(s => (
                    <tr key={s.id} className="border-b border-border hover:bg-surface">
                      <td className="py-3 px-4">{s.date}</td>
                      <td className="py-3 px-4">{s.demos_booked}</td>
                      <td className="py-3 px-4">{s.demos_showed}</td>
                      <td className="py-3 px-4 capitalize">{s.lead_quality}</td>
                      <td className="py-3 px-4">{s.show_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VentureCardContent>
      </VentureCard>
    </motion.div>
    </>
  );
}
